<template>
  <RevButtonBase
    :aria-disabled="disabled"
    :class="wrapperClassNames"
    :disabled
    v-bind="$attrs"
  >
    <label :class="labelClassNames" :for="selectId">
      <slot name="label"></slot>
    </label>
    <span :class="selectedItemsClassNames">
      <slot name="value"></slot>
    </span>
    <IconChevronDown :class="chevronClassNames" size="20" />
  </RevButtonBase>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { RevButtonBase } from '@ds/components/ButtonBase'
import { IconChevronDown } from '@ds/icons/IconChevronDown'
import type { SlotContent } from '@ds/types/SlotContent'
import { tw } from '@ds/utils/tailwind'

const props = defineProps<{
  /**
   * Indicates if the select is in error state.
   */
  hasError: boolean

  /**
   * Indicates if the select is disabled.
   */
  disabled: boolean

  /**
   * Indicates if the select is focused.
   */
  focused: boolean

  /**
   * Indicates if the select has at least one selected option.
   */
  hasSelectedOption: boolean

  /**
   * The unique HTML identifier of the select.
   * This will be used to link the actual select with its label (so that a click
   * on the label focuses the select).
   */
  selectId: string
}>()

defineOptions({
  name: 'BackBoxRangeInputTrigger',
  inheritAttrs: false,
})

defineSlots<{
  label: SlotContent
  value: SlotContent
}>()

const labelClassNames = computed(() => [
  tw`pointer-events-none absolute inset-x-12 top-0 flex items-center pr-16`,
  !props.disabled &&
    props.hasSelectedOption &&
    tw`text-static-default-low top-4 h-auto`,
  !props.disabled && !props.hasSelectedOption && tw`text-action-default-hi`,
  props.disabled && tw`text-static-default-low-disabled`,
  props.hasSelectedOption ? tw`caption` : tw`body-1 h-full`,
])

const selectedItemsClassNames = computed(() => [
  tw`rounded-sm body-1 absolute inset-x-0 bottom-2 size-full grow`,
  tw`appearance-none truncate border-0 bg-transparent text-start outline-none`,
])

const chevronClassNames = computed(() => [
  tw`pointer-events-none absolute right-12 shrink-0 select-none`,
  props.disabled && tw`text-static-default-low-disabled`,
  !props.disabled && props.hasSelectedOption && tw`text-static-default-low`,
  !props.disabled && !props.hasSelectedOption && tw`text-action-default-hi`,
])

const wrapperClassNames = computed(() => [
  tw`rounded-sm relative flex h-48 w-full items-center border px-12`,
  tw`ease-in-out motion-safe:transition-colors motion-safe:duration-200`,
  props.focused &&
    !props.hasError &&
    tw`border-action-default-low-focus outline-default-low`,
  props.focused &&
    props.hasError &&
    tw`border-action-danger-low-focus outline-danger-low`,
  !props.disabled &&
    tw`bg-static-default-low text-action-default-hi hover:bg-static-default-low-hover`,
  !props.disabled && props.hasError
    ? tw`border-action-danger-low`
    : tw`border-action-default-low`,
  props.disabled &&
    tw`bg-static-default-low-disabled border-action-default-low-disabled text-action-default-hi-disabled cursor-not-allowed`,
])
</script>
